"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { useRouter } from "next/navigation";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AuthModal from "@/app/homepage/components/AuthModal";
import { useAuth } from "@/contexts/AuthContext";
import { createClient } from "@/lib/supabase/client";

// ─── Machine generation ───────────────────────────────────────────────────────
export const PREFIXES = ["Nordic", "Fjord", "Arctic", "Boreal", "Polar", "Viking", "Norden", "Oslo", "Bergen", "Trondheim"];
export const SUFFIXES = ["Pro", "Max", "Ultra", "Prime", "X", "Z", "Plus", "Core", "Elite", "Flex"];
export const CATEGORIES = [
  "Industriell", "Tung maskin", "Presisjon", "Tech hardware",
  "Automasjon", "Energi", "CNC maskin", "Hydraulikk", "Pneumatikk", "Robotikk",
];
export const STATUSES = ["Aktiv", "Beta", "Vedlikehold", "Ny"] as const;
export const STATUS_TYPES: Record<string, string> = {
  "Aktiv": "active", "Beta": "beta", "Vedlikehold": "maintenance", "Ny": "new",
};

export interface Machine {
  id: string;
  name: string;
  category: string;
  spec: string;
  version: string;
  pricePerDay: number;
  status: string;
}

export function generateMachines(): Machine[] {
  const machines: Machine[] = [];
  const specTemplates = [
    (n: number) => `${(n % 900) + 100} kW`,
    (n: number) => `${(n % 800) + 200} tonn`,
    (n: number) => `${(n % 120) + 8} kjerner`,
    (n: number) => `${(n % 500) + 50} bar`,
    (n: number) => `${(n % 400) + 100} rpm`,
    (n: number) => `${(n % 60) + 10} m/s`,
  ];

  for (let i = 0; i < 15000; i++) {
    const prefix = PREFIXES[i % PREFIXES.length];
    const suffix = SUFFIXES[Math.floor(i / PREFIXES.length) % SUFFIXES.length];
    const num = (i % 999) + 1;
    const cat = CATEGORIES[i % CATEGORIES.length];
    const specFn = specTemplates[i % specTemplates.length];
    const major = (i % 6) + 1;
    const minor = (i % 9) + 1;
    const patch = (i % 12);
    const statusIdx = i % 10 < 6 ? 0 : i % 10 < 8 ? 3 : i % 10 < 9 ? 1 : 2;
    const price = Math.round(((i % 4500) + 500) / 10) * 10;

    machines.push({
      id: `SN-${String(i + 1).padStart(5, "0")}`,
      name: `${prefix} ${suffix} ${num}`,
      category: cat,
      spec: specFn(i),
      version: `v${major}.${minor}.${patch}`,
      pricePerDay: price,
      status: STATUSES[statusIdx],
    });
  }
  return machines;
}

export const ALL_MACHINES = generateMachines();
const PAGE_SIZE = 50;

// ─── Comparison Modal ─────────────────────────────────────────────────────────
function getPerformanceScore(machine: Machine): number {
  const seed = parseInt(machine.id.replace("SN-", ""), 10);
  return Math.round(60 + ((seed * 37) % 40));
}
function getUptimePct(machine: Machine): number {
  const seed = parseInt(machine.id.replace("SN-", ""), 10);
  return Math.round(85 + ((seed * 13) % 15));
}
function getEnergyKwh(machine: Machine): number {
  const seed = parseInt(machine.id.replace("SN-", ""), 10);
  return Math.round(20 + ((seed * 7) % 80));
}
function getWarrantyYears(machine: Machine): number {
  const seed = parseInt(machine.id.replace("SN-", ""), 10);
  return (seed % 4) + 1;
}

export interface CompareRow {
  label: string;
  key: string;
  getValue: (m: Machine) => string | number;
  higherIsBetter?: boolean;
  lowerIsBetter?: boolean;
}

export const COMPARE_ROWS: CompareRow[] = [
  { label: "Maskin navn", key: "name", getValue: (m) => m.name },
  { label: "Kategori", key: "category", getValue: (m) => m.category },
  { label: "Spesifikasjon", key: "spec", getValue: (m) => m.spec },
  { label: "Versjon", key: "version", getValue: (m) => m.version },
  { label: "Pris per dag", key: "price", getValue: (m) => m.pricePerDay, lowerIsBetter: true },
  { label: "Status", key: "status", getValue: (m) => m.status },
  { label: "Garanti (år)", key: "warranty", getValue: (m) => getWarrantyYears(m), higherIsBetter: true },
  { label: "Ytelsesscore", key: "perf", getValue: (m) => getPerformanceScore(m), higherIsBetter: true },
  { label: "Oppetid %", key: "uptime", getValue: (m) => getUptimePct(m), higherIsBetter: true },
  { label: "Energiforbruk kWh", key: "energy", getValue: (m) => getEnergyKwh(m), lowerIsBetter: true },
];

export function getBestIdx(machines: Machine[], row: CompareRow): number | null {
  if (!row.higherIsBetter && !row.lowerIsBetter) return null;
  const vals = machines.map((m) => Number(row.getValue(m)));
  if (vals.some(isNaN)) return null;
  if (row.higherIsBetter) {
    const max = Math.max(...vals);
    return vals.indexOf(max);
  }
  if (row.lowerIsBetter) {
    const min = Math.min(...vals);
    return vals.indexOf(min);
  }
  return null;
}

function ComparisonModal({ machines, onClose }: { machines: Machine[]; onClose: () => void }) {
  const router = useRouter();
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        backgroundColor: "rgba(0,0,0,0.85)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "20px",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          backgroundColor: "#0A0A0A",
          border: "1px solid rgba(255,255,255,0.12)",
          width: "100%",
          maxWidth: "900px",
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
        }}
      >
        {/* Header */}
        <div style={{ padding: "24px 28px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <p style={{ fontFamily: "Rajdhani, sans-serif", fontWeight: 700, fontSize: "1.1rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "white", margin: 0 }}>Sammenlign maskiner</p>
          <button onClick={onClose} className="btn-press btn-press-dark" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.6)", width: "32px", height: "32px", cursor: "pointer", fontSize: "16px", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ padding: "14px 20px", textAlign: "left", fontFamily: "Rajdhani, sans-serif", fontWeight: 700, fontSize: "0.7rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", borderBottom: "1px solid rgba(255,255,255,0.08)", width: "160px" }}>Egenskap</th>
                {machines.map((m) => (
                  <th key={m.id} style={{ padding: "14px 20px", textAlign: "left", fontFamily: "Rajdhani, sans-serif", fontWeight: 700, fontSize: "0.8rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "white", borderBottom: "1px solid rgba(255,255,255,0.08)", borderLeft: "1px solid rgba(255,255,255,0.06)" }}>
                    {m.name}
                    <div style={{ fontFamily: "Rajdhani, sans-serif", fontWeight: 400, fontSize: "0.65rem", color: "rgba(255,255,255,0.3)", letterSpacing: "0.1em", marginTop: "2px" }}>{m.id}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row) => {
                const bestIdx = getBestIdx(machines, row);
                return (
                  <tr key={row.key} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <td style={{ padding: "12px 20px", fontFamily: "Nunito Sans, sans-serif", fontWeight: 300, fontSize: "0.8rem", color: "rgba(255,255,255,0.45)", letterSpacing: "0.04em" }}>{row.label}</td>
                    {machines.map((m, idx) => {
                      const val = row.getValue(m);
                      const isBest = bestIdx === idx;
                      return (
                        <td key={m.id} style={{
                          padding: "12px 20px",
                          fontFamily: "Nunito Sans, sans-serif",
                          fontWeight: isBest ? 600 : 300,
                          fontSize: "0.85rem",
                          color: isBest ? "#4ade80" : "rgba(255,255,255,0.75)",
                          borderLeft: "1px solid rgba(255,255,255,0.06)",
                          backgroundColor: isBest ? "rgba(74,222,128,0.06)" : "transparent",
                        }}>
                          {row.key === "price" ? `${Number(val).toLocaleString("nb-NO")} kr` : String(val)}
                          {row.key === "uptime" ? "%" : ""}
                          {row.key === "perf" ? " / 100" : ""}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer with detail links */}
        <div style={{ padding: "20px 28px", borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", gap: "12px", flexWrap: "wrap" }}>
          {machines.map((m) => (
            <button
              key={m.id}
              className="btn-press btn-press-dark"
              onClick={() => { onClose(); router.push(`/maskiner/${m.id}`); }}
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.2)",
                color: "white",
                fontFamily: "Rajdhani, sans-serif",
                fontWeight: 600,
                fontSize: "0.75rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                padding: "10px 20px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                flex: 1,
                minWidth: "160px",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(255,255,255,0.06)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent"; }}
            >
              {m.name} — Vis detaljer →
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function MachinesInteractive() {
  const router = useRouter();
  const { user } = useAuth();
  const supabase = createClient();
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState<"login" | "signup">("login");
  const [toast, setToast] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("Alle");
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  // Comparison state
  const [selected, setSelected] = useState<string[]>([]);
  const [compareOpen, setCompareOpen] = useState(false);

  // Saved/star state
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Load saved machine IDs from Supabase
  useEffect(() => {
    if (!user) { setSavedIds(new Set()); return; }
    const load = async () => {
      const { data } = await supabase.from('saved_machines').select('catalog_id').eq('user_id', user.id);
      if (data) setSavedIds(new Set(data.map((r: { catalog_id: string }) => r.catalog_id)));
    };
    load();
  }, [user]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  const openLogin = () => { setAuthTab("login"); setAuthOpen(true); };
  const openSignup = () => { setAuthTab("signup"); setAuthOpen(true); };

  // Toggle save/unsave
  const handleToggleSave = useCallback(async (machine: Machine, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) { setAuthTab('login'); setAuthOpen(true); return; }
    if (savingId === machine.id) return;
    setSavingId(machine.id);
    const isSaved = savedIds.has(machine.id);
    if (isSaved) {
      setSavedIds((prev) => { const n = new Set(prev); n.delete(machine.id); return n; });
      await supabase.from('saved_machines').delete().eq('user_id', user.id).eq('catalog_id', machine.id);
      showToast('Maskin fjernet fra lagrede');
    } else {
      setSavedIds((prev) => new Set([...prev, machine.id]));
      await supabase.from('saved_machines').insert({ user_id: user.id, catalog_id: machine.id, machine_name: machine.name, category: machine.category, specs: machine.spec });
      showToast('Maskin lagret!');
    }
    setSavingId(null);
  }, [user, savedIds, savingId, supabase, showToast]);

  const filtered = useMemo(() => {
    let result = ALL_MACHINES;
    if (activeCategory !== "Alle") {
      const catMap: Record<string, string> = {
        "Industriell": "Industriell",
        "Tung maskin": "Tung maskin",
        "Presisjon": "Presisjon",
        "Tech": "Tech hardware",
        "Automasjon": "Automasjon",
        "Energi": "Energi",
        "CNC": "CNC maskin",
      };
      const target = catMap[activeCategory] || activeCategory;
      result = result.filter((m) => m.category === target);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.id.toLowerCase().includes(q) ||
          m.category.toLowerCase().includes(q) ||
          m.spec.toLowerCase().includes(q)
      );
    }
    return result;
  }, [search, activeCategory]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search, activeCategory]);

  const filterButtons = ["Alle", "Industriell", "Tung maskin", "Presisjon", "Tech", "Automasjon", "Energi", "CNC"];

  // Checkbox handlers
  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) return prev; // max 3
      return [...prev, id];
    });
  }, []);

  const removeSelected = useCallback((id: string) => {
    setSelected((prev) => prev.filter((x) => x !== id));
  }, []);

  const clearSelected = useCallback(() => setSelected([]), []);

  const selectedMachines = useMemo(
    () => selected.map((id) => ALL_MACHINES.find((m) => m.id === id)).filter(Boolean) as Machine[],
    [selected]
  );

  return (
    <>
      <Header onLoginClick={openLogin} onSignupClick={openSignup} transparent={false} />

      {toast && <div className="toast">{toast}</div>}

      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultTab={authTab}
        showToast={showToast}
      />

      {compareOpen && selectedMachines.length >= 2 && (
        <ComparisonModal machines={selectedMachines} onClose={() => setCompareOpen(false)} />
      )}

      <style>{`
        @media (max-width: 768px) {
          .machines-header-title { font-size: 24px !important; }
          .machines-header-count { font-size: 1.25rem !important; }
          .machines-header-row { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; }
          .machines-filters-row { flex-direction: column !important; align-items: stretch !important; gap: 10px !important; }
          .machines-search-input { width: 100% !important; }
          .machines-filter-buttons { display: flex !important; flex-wrap: wrap !important; gap: 6px !important; }
          .machines-filter-btn { font-size: 0.7rem !important; padding: 6px 10px !important; }
          .machines-table-wrapper { display: none !important; }
          .machines-cards-wrapper { display: flex !important; flex-direction: column !important; gap: 0 !important; }
          .machines-pagination-page-numbers { display: none !important; }
          .machines-pagination-info { display: none !important; }
          .machines-pagination-mobile-indicator { display: block !important; }
          .machines-pagination-btn { padding: 6px 10px !important; font-size: 11px !important; }
          .machines-pagination-row { justify-content: center !important; gap: 12px !important; }
        }
        @media (min-width: 769px) {
          .machines-cards-wrapper { display: none !important; }
          .machines-pagination-mobile-indicator { display: none !important; }
        }
        .compare-checkbox {
          width: 16px;
          height: 16px;
          cursor: pointer;
          accent-color: white;
          flex-shrink: 0;
        }
        .compare-checkbox:disabled { cursor: not-allowed; opacity: 0.3; }
        .compare-bar-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.15);
          padding: 4px 10px;
          font-family: Rajdhani, sans-serif;
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: white;
        }
        .compare-bar-chip-x {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.5);
          cursor: pointer;
          font-size: 12px;
          padding: 0;
          line-height: 1;
        }
        .compare-bar-chip-x:hover { color: white; }
        .star-btn {
          background: transparent;
          border: none;
          cursor: pointer;
          font-size: 16px;
          padding: 0 4px;
          line-height: 1;
          transition: transform 0.15s ease;
        }
        .star-btn:hover { transform: scale(1.2); }
      `}</style>

      <main
        style={{
          backgroundColor: "#0A0A0A",
          minHeight: "100vh",
          paddingTop: "100px",
          paddingBottom: selected.length > 0 ? "120px" : "80px",
        }}
      >
        <div className="max-w-7xl mx-auto px-6">

          {/* Header */}
          <div style={{ marginBottom: "48px", paddingBottom: "48px", borderBottom: "1px solid #2A2A2A" }}>
            <p className="section-label" style={{ marginBottom: "12px" }}>Katalog</p>
            <div className="machines-header-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <h1
                className="machines-header-title"
                style={{
                  fontFamily: "Rajdhani, sans-serif",
                  fontWeight: 700,
                  fontSize: "clamp(2.5rem, 5vw, 4rem)",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "white",
                  lineHeight: 0.95,
                }}
              >
                Maskinkatalog
              </h1>
              <div style={{ textAlign: "right" }}>
                <p
                  className="machines-header-count"
                  style={{
                    fontFamily: "Rajdhani, sans-serif",
                    fontWeight: 700,
                    fontSize: "2rem",
                    color: "white",
                    letterSpacing: "0.05em",
                  }}
                >
                  {filtered.length.toLocaleString("nb-NO")}
                </p>
                <p className="section-label">maskiner funnet</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div
            className="machines-filters-row"
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              marginBottom: "32px",
              alignItems: "center",
            }}
          >
            <div style={{ position: "relative", flexShrink: 0 }}>
              <input
                type="text"
                placeholder="Søk maskiner..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="machines-search-input"
                style={{
                  background: "transparent",
                  border: "1px solid #2A2A2A",
                  color: "white",
                  fontFamily: "Rajdhani, sans-serif",
                  fontSize: "0.8rem",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  padding: "10px 20px",
                  width: "240px",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                }}
                onFocus={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.4)"; }}
                onBlur={(e) => { e.target.style.borderColor = "#2A2A2A"; }}
              />
            </div>
            <div className="machines-filter-buttons" style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {filterButtons.map((cat) => (
                <button
                  key={cat}
                  className={`filter-btn machines-filter-btn btn-press btn-press-dark ${activeCategory === cat ? "active" : ""}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Desktop Table */}
          <div className="machines-table-wrapper" style={{ overflowX: "auto" }}>
            <table className="machine-table">
              <thead>
                <tr>
                  <th style={{ width: "40px" }}></th>
                  <th style={{ width: "36px" }}></th>
                  <th>ID</th>
                  <th>Maskin</th>
                  <th>Kategori</th>
                  <th>Spesifikasjon</th>
                  <th>Versjon</th>
                  <th>Kr/dag</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: "center", padding: "60px", color: "rgba(255,255,255,0.3)", fontFamily: "Rajdhani, sans-serif", letterSpacing: "0.1em", textTransform: "uppercase", fontSize: "0.85rem" }}>
                      Ingen maskiner funnet
                    </td>
                  </tr>
                ) : (
                  paginated.map((machine) => {
                    const isChecked = selected.includes(machine.id);
                    const isDisabled = !isChecked && selected.length >= 3;
                    const isSaved = savedIds.has(machine.id);
                    return (
                      <tr
                        key={machine.id}
                        style={{ cursor: "pointer" }}
                        onClick={() => router.push(`/maskiner/${machine.id}`)}
                      >
                        <td
                          style={{ textAlign: "center" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div style={{ position: "relative", display: "inline-block" }}>
                            <input
                              type="checkbox"
                              className="compare-checkbox"
                              checked={isChecked}
                              disabled={isDisabled}
                              onChange={() => toggleSelect(machine.id)}
                              title={isDisabled ? "Maks 3 maskiner" : ""}
                            />
                          </div>
                        </td>
                        <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                          <button
                            className="star-btn"
                            onClick={(e) => handleToggleSave(machine, e)}
                            title={isSaved ? "Fjern fra lagrede" : "Lagre maskin"}
                            style={{ color: isSaved ? '#facc15' : 'rgba(255,255,255,0.25)' }}
                          >
                            {isSaved ? '★' : '☆'}
                          </button>
                        </td>
                        <td style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.75rem", letterSpacing: "0.1em", color: "rgba(255,255,255,0.35)", fontWeight: 600 }}>
                          {machine.id}
                        </td>
                        <td style={{ fontFamily: "Rajdhani, sans-serif", fontWeight: 600, fontSize: "0.9rem", color: "white", letterSpacing: "0.04em" }}>
                          {machine.name}
                        </td>
                        <td style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem" }}>{machine.category}</td>
                        <td style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.85rem", color: "rgba(255,255,255,0.7)", letterSpacing: "0.04em" }}>{machine.spec}</td>
                        <td style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.8rem", color: "rgba(255,255,255,0.4)", letterSpacing: "0.08em" }}>{machine.version}</td>
                        <td style={{ fontFamily: "Rajdhani, sans-serif", fontWeight: 600, fontSize: "0.9rem", color: "white", letterSpacing: "0.04em" }}>
                          {machine.pricePerDay.toLocaleString("nb-NO")} kr
                        </td>
                        <td>
                          <span className={`badge badge-${STATUS_TYPES[machine.status] || "active"}`}>{machine.status}</span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="machines-cards-wrapper">
            {paginated.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 0", color: "rgba(255,255,255,0.3)", fontFamily: "Rajdhani, sans-serif", letterSpacing: "0.1em", textTransform: "uppercase", fontSize: "0.85rem" }}>
                Ingen maskiner funnet
              </div>
            ) : (
              paginated.map((machine) => {
                const isChecked = selected.includes(machine.id);
                const isDisabled = !isChecked && selected.length >= 3;
                const isSaved = savedIds.has(machine.id);
                return (
                  <div
                    key={machine.id}
                    style={{
                      backgroundColor: "#0A0A0A",
                      border: `1px solid ${isChecked ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.12)"}`,
                      borderRadius: 0,
                      padding: "16px",
                      width: "100%",
                      marginBottom: "8px",
                    }}
                  >
                    {/* Checkbox + name + star row */}
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                      <input
                        type="checkbox"
                        className="compare-checkbox"
                        checked={isChecked}
                        disabled={isDisabled}
                        onChange={() => toggleSelect(machine.id)}
                        title={isDisabled ? "Maks 3 maskiner" : ""}
                      />
                      <p
                        style={{
                          fontFamily: "Rajdhani, sans-serif",
                          fontWeight: 700,
                          fontSize: "14px",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          color: "white",
                          margin: 0,
                          cursor: "pointer",
                          flex: 1,
                        }}
                        onClick={() => router.push(`/maskiner/${machine.id}`)}
                      >
                        {machine.name}
                      </p>
                      <button
                        className="star-btn"
                        onClick={(e) => handleToggleSave(machine, e)}
                        title={isSaved ? "Fjern fra lagrede" : "Lagre maskin"}
                        style={{ color: isSaved ? '#facc15' : 'rgba(255,255,255,0.25)', fontSize: '18px' }}
                      >
                        {isSaved ? '★' : '☆'}
                      </button>
                    </div>
                    <p style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.7rem", letterSpacing: "0.1em", color: "rgba(255,255,255,0.3)", margin: "0 0 10px 0" }}>
                      {machine.id}
                    </p>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px", alignItems: "center" }}>
                      <span style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.65rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", border: "1px solid #2A2A2A", padding: "2px 8px" }}>{machine.category}</span>
                      <span className={`badge badge-${STATUS_TYPES[machine.status] || "active"}`}>{machine.status}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                      <span style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.8rem", color: "rgba(255,255,255,0.6)", letterSpacing: "0.04em" }}>{machine.spec}</span>
                      <span style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.75rem", color: "rgba(255,255,255,0.35)", letterSpacing: "0.08em" }}>{machine.version}</span>
                    </div>
                    <div style={{ borderTop: "1px solid #1A1A1A", paddingTop: "10px" }}>
                      <span style={{ fontFamily: "Rajdhani, sans-serif", fontWeight: 700, fontSize: "1rem", color: "white", letterSpacing: "0.04em" }}>
                        {machine.pricePerDay.toLocaleString("nb-NO")} kr/dag
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "40px", paddingTop: "32px", borderTop: "1px solid #2A2A2A" }}
              className="machines-pagination-row"
            >
              <p className="machines-pagination-info" style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.75rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)" }}>
                Side {page} av {totalPages} — {filtered.length.toLocaleString("nb-NO")} maskiner
              </p>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="machines-pagination-btn btn-press btn-press-dark"
                  style={{ background: "transparent", border: "1px solid #2A2A2A", color: page === 1 ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.6)", fontFamily: "Rajdhani, sans-serif", fontWeight: 600, fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase", padding: "8px 20px", cursor: page === 1 ? "not-allowed" : "pointer", transition: "all 0.2s ease" }}>
                  ← Forrige
                </button>
                <div className="machines-pagination-page-numbers" style={{ display: "flex", gap: "8px" }}>
                  {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 7) pageNum = i + 1;
                    else if (page <= 4) pageNum = i + 1;
                    else if (page >= totalPages - 3) pageNum = totalPages - 6 + i;
                    else pageNum = page - 3 + i;
                    return (
                      <button key={pageNum} onClick={() => setPage(pageNum)}
                        style={{ background: page === pageNum ? "white" : "transparent", border: "1px solid", borderColor: page === pageNum ? "white" : "#2A2A2A", color: page === pageNum ? "#0A0A0A" : "rgba(255,255,255,0.4)", fontFamily: "Rajdhani, sans-serif", fontWeight: 600, fontSize: "0.75rem", letterSpacing: "0.08em", width: "36px", height: "36px", cursor: "pointer", transition: "all 0.15s ease" }}>
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <span className="machines-pagination-mobile-indicator" style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", padding: "0 4px" }}>
                  Side {page} av {totalPages}
                </span>
                <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="machines-pagination-btn btn-press btn-press-dark"
                  style={{ background: "transparent", border: "1px solid #2A2A2A", color: page === totalPages ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.6)", fontFamily: "Rajdhani, sans-serif", fontWeight: 600, fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase", padding: "8px 20px", cursor: page === totalPages ? "not-allowed" : "pointer", transition: "all 0.2s ease" }}>
                  Neste →
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Sticky Comparison Bar */}
      {selected.length > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 200,
            backgroundColor: "#111111",
            borderTop: "1px solid rgba(255,255,255,0.12)",
            padding: "14px 24px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontFamily: "Rajdhani, sans-serif", fontWeight: 700, fontSize: "0.7rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", flexShrink: 0 }}>
            Valgt:
          </span>
          {selectedMachines.map((m) => (
            <span key={m.id} className="compare-bar-chip">
              {m.name}
              <button className="compare-bar-chip-x" onClick={() => removeSelected(m.id)}>✕</button>
            </span>
          ))}
          <div style={{ marginLeft: "auto", display: "flex", gap: "10px", flexShrink: 0 }}>
            <button
              onClick={clearSelected}
              className="btn-press btn-press-dark"
              style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.5)", fontFamily: "Rajdhani, sans-serif", fontWeight: 600, fontSize: "0.7rem", letterSpacing: "0.12em", textTransform: "uppercase", padding: "8px 16px", cursor: "pointer" }}
            >
              Tøm utvalg
            </button>
            <button
              onClick={() => setCompareOpen(true)}
              className="btn-press"
              disabled={selected.length < 2}
              style={{
                background: selected.length >= 2 ? "white" : "rgba(255,255,255,0.1)",
                border: "1px solid",
                borderColor: selected.length >= 2 ? "white" : "rgba(255,255,255,0.1)",
                color: selected.length >= 2 ? "#0A0A0A" : "rgba(255,255,255,0.3)",
                fontFamily: "Rajdhani, sans-serif",
                fontWeight: 700,
                fontSize: "0.75rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                padding: "8px 20px",
                cursor: selected.length >= 2 ? "pointer" : "not-allowed",
                transition: "all 0.2s ease",
              }}
            >
              Sammenlign ({selected.length})
            </button>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}